import type { GenUIEvent } from '../core/events';
import { textDelta, componentEvent, doneEvent } from '../core/events';
import { isFinalResponse } from '../core/parser';

/**
 * Process a ReadableStream (from fetch response) and yield GenUIEvents
 */
export async function* processReadableStream(
    stream: ReadableStream<Uint8Array>
): AsyncGenerator<GenUIEvent> {
    const reader = stream.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
        while (true) {
            const { done, value } = await reader.read();

            if (done) {
                yield doneEvent();
                break;
            }

            buffer += decoder.decode(value, { stream: true });

            // Process complete lines
            const lines = buffer.split('\n');
            buffer = lines.pop() ?? ''; // Keep incomplete line in buffer

            for (const line of lines) {
                const trimmed = line.trim();
                if (!trimmed) continue;

                // Handle SSE format
                if (trimmed.startsWith('data: ')) {
                    const data = trimmed.slice(6);

                    if (data === '[DONE]') {
                        yield doneEvent();
                        return;
                    }

                    try {
                        const parsed = JSON.parse(data);

                        // Check if it's a final response with components
                        if (isFinalResponse(parsed)) {
                            for (const component of parsed.data.components) {
                                yield componentEvent(component);
                            }
                            yield doneEvent();
                            return;
                        }

                        // Otherwise, it's a streaming chunk
                        if (parsed.delta?.content) {
                            yield textDelta(parsed.delta.content);
                        }
                    } catch {
                        // Skip malformed JSON
                    }
                }
            }
        }
    } finally {
        reader.releaseLock();
    }
}
